import React, {useEffect, useState, useRef} from "react";
import "./style.css";
import SwitchSelect from "../../../../components/SwitchSelect";
import HeightModel from "../../../../domin/questionnaire/weight-loss/Height";
import { convertHeight } from "../../../../utils/common";
import { FloatingContinueBtn } from "../../../../components/FloatingContinueBtn";

export const Height = ({ clickNext, clicked, setAllow, data, isCurPage, onPageFocus, onPageKeyDown, inputRef }) => {
  const [unit, setUnit] = useState(data?.height?.unit);
  const [height, setHeight] = useState(data?.height?.height1 || '');
  const [height2, setHeight2] = useState(data?.height?.height2 || '');
  // 保存精确的厘米小数值用于计算，不用于显示
  const [heightPrecise, setHeightPrecise] = useState(data?.height?.height1 || '');
  // 保存精确的英寸小数值用于计算，不用于显示
  const [height2Precise, setHeight2Precise] = useState(data?.height?.height2 || '');
  
  // 英寸输入框的ref，用于自动聚焦
  const inchInputRef = useRef(null);
  
  // 控制浮动按钮显示
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);

  // 验证并限制cm值范围：90-243
  const validateCM = (value) => {
    if (value === '') return '';
    const num = parseInt(value);
    if (isNaN(num)) return '';
    if (num < 0) return '0';
    if (num > 243) return '243';
    return value;
  };

  // 验证并限制ft值范围：3-7
  const validateFT = (value) => {
    if (value === '') return '';
    const num = parseInt(value);
    if (isNaN(num)) return '';
    if (num < 3) return '3';
    if (num > 7) return '7';
    return value;
  };

  // 验证并限制in值范围：0-11
  const validateIN = (value) => {
    if (value === '') return '';
    const num = parseInt(value);
    if (isNaN(num)) return '';
    if (num < 0) return '0';
    if (num > 11) return '11';
    return value;
  };

  // 处理cm输入
  const handleCMChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const validated = validateCM(value);
    setHeight(validated);
    setHeightPrecise(validated);
  };

  // 处理ft输入
  const handleFTChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const validated = validateFT(value);
    setHeight(validated);
    setHeightPrecise(validated);
    
    // 如果输入的值在合法范围内（3-7），自动聚焦到英寸输入框
    const num = parseInt(validated);
    if (!isNaN(num) && num >= 3 && num <= 7 && inchInputRef.current) {
      setTimeout(() => {
        inchInputRef.current?.focus();
      }, 0);
    }
  };

  // 处理in输入
  const handleINChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const validated = validateIN(value);
    setHeight2(validated);
    setHeight2Precise(validated);
  };

  useEffect(() => {
    if (clicked) {
      const h1 = Number(height);
      const h2 = Number(height2 || 0);
      const isValid = unit === 'cm'
        ? h1 >= 90 && h1 <= 243
        : (h1 >= 3 && h1 <= 7) && (h2 >= 0 && h2 <= 11);
      if (isValid) {
        clickNext(new HeightModel(height, height2 || 0, unit));
      }
    }
  }, [clicked]);

  useEffect(() => {
    const h1 = Number(height);
    const h2 = Number(height2 || 0);
    const isValid = unit === 'cm'
      ? !Number.isNaN(h1) && h1 >= 90 && h1 <= 243
      : !Number.isNaN(h1) && h1 >= 3 && h1 <= 7 && !Number.isNaN(h2) && h2 >= 0 && h2 <= 11;
    setAllow(isValid);
  }, [height, height2, unit]);
  
  // 处理输入框聚焦
  const handleFocus = (e) => {
    setShowFloatingBtn(true);
    if (onPageFocus) onPageFocus(e);
  };
  
  // 处理输入框失焦
  const handleBlur = () => {
    // 延迟时间增加到 200ms，避免焦点切换时误隐藏
    setTimeout(() => {
      // 检查是否有输入框仍然聚焦
      const ftFocused = inputRef.current === document.activeElement;
      const inFocused = inchInputRef.current === document.activeElement;
      
      if (!ftFocused && !inFocused) {
        setShowFloatingBtn(false);
      }
    }, 200);
  };
  
  // 处理浮动按钮点击
  const handleFloatingBtnClick = () => {
    const h1 = Number(height);
    const h2 = Number(height2 || 0);
    const isValid = unit === 'cm'
      ? h1 >= 90 && h1 <= 243
      : (h1 >= 3 && h1 <= 7) && (h2 >= 0 && h2 <= 11);
    if (isValid) {
      // 隐藏键盘
      if (inputRef.current) inputRef.current.blur();
      if (inchInputRef.current) inchInputRef.current.blur();
      setShowFloatingBtn(false);
      // 触发提交
      clickNext(new HeightModel(height, height2 || 0, unit));
    }
  };

  return (
    <div className="height" data-model-id="16755:787">
      <FloatingContinueBtn 
        show={showFloatingBtn} 
        allow={unit === 'cm' 
          ? !isNaN(Number(height)) && Number(height) >= 90 && Number(height) <= 243
          : !isNaN(Number(height)) && Number(height) >= 3 && Number(height) <= 7 && !isNaN(Number(height2 || 0)) && Number(height2 || 0) >= 0 && Number(height2 || 0) <= 11
        }
        onClick={handleFloatingBtnClick}
      />

      <div className="frame-4">
        <div className="title">
          <div className="heading">What's your height?</div>
        </div>

        <div className="frame-5">
          <SwitchSelect data={["ft", "cm"]} onClick={(newUnit) => {
            const oldUnit = unit;
            setUnit(newUnit);
            
            if (oldUnit === 'cm' && newUnit === 'ft') {
              // cm -> ft: 将厘米转换为英尺和英寸
              if (height) {
                // 使用精确值进行计算，如果没有则使用显示值
                const cm = parseFloat(heightPrecise) || parseFloat(height);
                const totalFeet = cm / 30.48; // 总英尺数（含小数）
                let feet = Math.floor(totalFeet); // 英尺整数部分
                let inchesDecimal = (totalFeet - feet) * 12; // 英寸（小数部分 * 12）
                let inchesDisplay = Math.round(inchesDecimal); // 显示用：四舍五入到整数
                
                // 如果英寸 >= 12，进位到英尺
                if (inchesDisplay >= 12) {
                  feet += 1;
                  inchesDisplay = 0;
                  inchesDecimal = 0; // 进位后精确值也归零
                }
                
                // 应用范围限制：ft 3-7
                if (feet < 3) {
                  feet = 3;
                  inchesDisplay = 0;
                  inchesDecimal = 0;
                } else if (feet > 7) {
                  feet = 7;
                  inchesDisplay = 11;
                  inchesDecimal = 11;
                }
                
                setHeight(feet.toString());
                setHeight2(inchesDisplay.toString()); // 显示整数
                setHeight2Precise(inchesDecimal.toString()); // 保存精确值
              }
            } else if (oldUnit === 'ft' && newUnit === 'cm') {
              // ft -> cm: 将英尺和英寸转换为厘米
              if (height) {
                const feet = parseFloat(height) || 0;
                // 使用精确值进行计算，如果没有则使用显示值
                const inches = parseFloat(height2Precise) || parseFloat(height2) || 0;
                const totalFeet = feet + inches / 12;
                let cmDecimal = totalFeet * 30.48; // 精确的厘米值
                let cmDisplay = Math.round(cmDecimal); // 显示用：四舍五入到整数
                
                // 应用范围限制：cm 90-243
                if (cmDisplay < 90) {
                  cmDisplay = 90;
                  cmDecimal = 90;
                } else if (cmDisplay > 243) {
                  cmDisplay = 243;
                  cmDecimal = 243;
                }
                
                setHeight(cmDisplay.toString()); // 显示整数
                setHeightPrecise(cmDecimal.toString()); // 保存精确值
                setHeight2('');
                setHeight2Precise('');
              }
            }
          }} value={unit} />

          <div className="age-container">
            <div className="div-2">
              <input 
                ref={inputRef} 
                className="height-label" 
                value={height} 
                placeholder={"Height"} 
                onChange={unit === 'cm' ? handleCMChange : handleFTChange}
                onFocus={handleFocus} 
                onBlur={handleBlur}
                onKeyDown={onPageKeyDown} 
                type={"number"} 
                inputMode="numeric" 
                step="1" 
                min="0" 
              />

              <div className="unit-label-2">{unit === "ft" ? "ft" : "cm"}</div>
            </div>

            {unit === 'ft' && <div className="div-2">
              <input 
                ref={inchInputRef}
                className="height-label" 
                value={height2} 
                placeholder={"Height"} 
                onChange={handleINChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={onPageKeyDown} 
                type={"number"} 
                inputMode="numeric" 
                step="1" 
                min="0" 
              />

              <div className="unit-label-2">{unit === "ft" ? "in" : "cm"}</div>
            </div>}
          </div>
        </div>

        <div className="info-container">
          <div className="info-row">
            <p className="info-text">Calculating your body mass index</p>

            <p className="info-description">
              Body mass index (BMI) is a measure of body fat based on height and
              weight.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
